const ASSET_BASE = "//asset.travelfactory.fr"
const DEFAULT_LOCALE = "fr_FR"

/**
 * Pick localized string from a locale-keyed object.
 * @param {Record<string, string>|undefined} obj
 * @param {string} locale
 * @param {string} [fallback]
 * @returns {string}
 */
function pickLocalized(obj, locale, fallback = DEFAULT_LOCALE) {
    if (!obj || typeof obj !== "object") {
        return ""
    }
    return obj[locale] || obj[fallback] || ""
}

/**
 * Prefix relative asset paths with the asset CDN base.
 * @param {string|undefined|null} path
 * @returns {string|undefined}
 */
function toAssetUrl(path) {
    if (!path || typeof path !== "string") {
        return undefined
    }
    if (path.startsWith("//") || path.startsWith("http://") || path.startsWith("https://")) {
        return path
    }
    return `${ASSET_BASE}${path.startsWith("/") ? "" : "/"}${path}`
}

/**
 * Normalize picture objects from the new API to the legacy shape.
 * @param {Array<Record<string, unknown>>|undefined} images
 * @returns {Array<Record<string, unknown>>}
 */
function mapPictures(images) {
    if (!Array.isArray(images)) {
        return []
    }
    return images.map((image, index) => ({
        img: image.img,
        imgXL: image.imgXL,
        imgSmall: image.imgSmall,
        imgMedium: image.imgMedium,
        imgLarge: image.imgLarge,
        imgNumber: index + 1,
    }))
}

/**
 * Sum slope counts from slopesByDifficulty.
 * @param {Record<string, number>|undefined} slopesByDifficulty
 * @returns {string|undefined}
 */
function sumSlopes(slopesByDifficulty) {
    if (!slopesByDifficulty || typeof slopesByDifficulty !== "object") {
        return undefined
    }
    const total = Object.values(slopesByDifficulty).reduce(
        (sum, value) => sum + (Number(value) || 0),
        0
    )
    return total > 0 ? String(total) : undefined
}

/**
 * @param {unknown} value
 * @returns {string|undefined}
 */
function toOptionalString(value) {
    return value != null ? String(value) : undefined
}

/**
 * Map content.reasons[] to flat reason1title/body … reason5title/body.
 * @param {Array<{number: number, title?: Record<string, string>, body?: Record<string, string>}>|undefined} reasons
 * @param {string} locale
 * @returns {Record<string, string>}
 */
function mapReasons(reasons, locale) {
    const result = {}
    if (!Array.isArray(reasons)) {
        return result
    }
    for (const r of reasons) {
        const n = r.number
        if (n >= 1 && n <= 5) {
            result[`reason${n}title`] = pickLocalized(r.title, locale)
            result[`reason${n}body`] = pickLocalized(r.body, locale)
        }
    }
    return result
}

/**
 * Map catalog-manager-secure station response to the legacy flat shape
 * expected by station-domain and responsible-stay Vue components.
 * @param {Record<string, unknown>|undefined|null} station
 * @param {string} locale
 * @returns {Record<string, unknown>}
 */
export function mapStationToLegacyFormat(station, locale) {
    if (!station || typeof station !== "object") {
        return {}
    }

    const effectiveLocale = locale || DEFAULT_LOCALE
    const names = station.names || {}
    const content = station.content || {}
    const media = station.media || {}
    const urls = station.urls || {}
    const domainStats = station.domainStats || {}
    const slopesByDifficulty = station.slopesByDifficulty || {}

    const altitudeMin = station.altitudeMin
    const altitudeMax = station.altitudeMax
    const computedAltitudeDiff =
        altitudeMin != null && altitudeMax != null
            ? altitudeMax - altitudeMin
            : undefined
    const altitudeDiff = domainStats.altitudeDiff ?? computedAltitudeDiff

    const infoSummer = pickLocalized(content.infoSummer, effectiveLocale)
    const winterImages = mapPictures(media.winterImagesRetro || media.winterImages)
    const summerImages = mapPictures(media.summerImagesRetro || media.summerImages)

    return {
        id: station.stationId != null ? String(station.stationId) : undefined,
        stationId: station.stationId != null ? String(station.stationId) : undefined,
        name: station.stationName,
        parentStationId: station.stationMereId,
        placeType: station.type,

        countryName: pickLocalized(names.country, effectiveLocale),
        countryId: station.countryId,
        departementName: pickLocalized(names.department, effectiveLocale),
        departementId: station.departmentId,
        regionId: station.regionId,
        region: pickLocalized(names.region, effectiveLocale),

        nameDomain: pickLocalized(names.domain, effectiveLocale),
        connectedNameDomain:
            pickLocalized(station.connectedNameDomain, effectiveLocale) ||
            pickLocalized(names.relatedDomain, effectiveLocale),
        connectedDomainDescription: pickLocalized(
            station.connectedDomainDescription,
            effectiveLocale
        ),
        domaineReliePistLength: toOptionalString(station.domaineReliePistLength),
        connectedDomainNbSlopes: toOptionalString(station.connectedDomainNbSlopes),
        nbConnectedDomainSkiLifts: toOptionalString(station.nbConnectedDomainSkiLifts),
        connectedDomainNbSlopesGreen: toOptionalString(
            station.connectedDomainNbSlopesGreen
        ),
        connectedDomainNbSlopesBlue: toOptionalString(
            station.connectedDomainNbSlopesBlue
        ),
        connectedDomainNbSlopesRed: toOptionalString(
            station.connectedDomainNbSlopesRed
        ),
        connectedDomainNbSlopesBlack: toOptionalString(
            station.connectedDomainNbSlopesBlack
        ),
        connectedDomainSnowpark: pickLocalized(
            station.connectedDomainSnowpark,
            effectiveLocale
        ),
        connectedDomainMap: toAssetUrl(station.connectedDomainMap),
        domaineRelieSkiFond: toOptionalString(station.domaineRelieSkiFond),
        domaineId: station.domainId,
        criterias: station.criterias || [],

        infoWinter: pickLocalized(content.infoWinter, effectiveLocale),
        infoSummer,
        infoWSummer: infoSummer,
        infoStation: pickLocalized(content.generalStationInformation, effectiveLocale),
        lieuxvisiter: pickLocalized(content.placesToVisit, effectiveLocale),
        culture: pickLocalized(content.cultureAndMuseums, effectiveLocale),
        sportiveActivity: pickLocalized(content.sportiveActivities, effectiveLocale),
        afterSki: pickLocalized(content.afterSki, effectiveLocale),
        pratique: pickLocalized(content.pratique, effectiveLocale),
        description: pickLocalized(content.description, effectiveLocale),

        ...mapReasons(content.reasons, effectiveLocale),

        route: pickLocalized(content.arrivalByRoad, effectiveLocale),
        train: pickLocalized(content.arrivalByTrain, effectiveLocale),
        avion: pickLocalized(content.arrivalByPlane, effectiveLocale),

        masterUrl:
            station.masterUrl ||
            pickLocalized(urls.masterUrls?.TS, effectiveLocale) ||
            undefined,
        placeUrl:
            station.placeUrl ||
            pickLocalized(urls.placeUrls?.TS, effectiveLocale) ||
            undefined,

        pisteLength:
            station.totalSlopesKm != null ? String(station.totalSlopesKm) : undefined,
        nbDomainSkiLifts:
            station.numberOfLifts != null ? String(station.numberOfLifts) : undefined,
        nbSlopesGreen:
            slopesByDifficulty.green != null
                ? String(slopesByDifficulty.green)
                : undefined,
        nbSlopesBlue:
            slopesByDifficulty.blue != null
                ? String(slopesByDifficulty.blue)
                : undefined,
        nbSlopesRed:
            slopesByDifficulty.red != null
                ? String(slopesByDifficulty.red)
                : undefined,
        nbSlopesBlack:
            slopesByDifficulty.black != null
                ? String(slopesByDifficulty.black)
                : undefined,
        nbSlopes:
            domainStats.nbSlopes != null
                ? String(domainStats.nbSlopes)
                : sumSlopes(slopesByDifficulty),

        minAltitude: altitudeMin,
        maxAltitude: altitudeMax,
        altitudeDiff,

        snowPark: pickLocalized(domainStats.snowPark, effectiveLocale),
        nbKmCrossCountrySkiing:
            domainStats.crossCountryKm != null
                ? String(domainStats.crossCountryKm)
                : undefined,

        picturesPlace: winterImages,
        picturesPlaceWinter: winterImages,
        picturesPlaceSummer: summerImages,

        icon: toAssetUrl(media.icon),
        iconWhite: toAssetUrl(media.iconWhite),
        marketingImage: toAssetUrl(media.marketingImage),
        cardMarketingImage: toAssetUrl(media.cardMarketingImage),
        domainMap: toAssetUrl(
            domainStats.domainMap || media.map || media.slopMap
        ),
    }
}
